import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    chartCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.6s ease',
    },
    chartTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    noDataText: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    summaryItem: {
        textAlign: 'center',
    },
    summaryLabel: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    summaryValue: {
        fontSize: '1.4rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
}));

function FlowAnalyticsChart({ loading, data }) {
    const classes = useStyles();
    const theme = useTheme();
    const chartRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationCount, setAnimationCount] = useState(0);
    const [chartKey, setChartKey] = useState(0);

    useEffect(() => {
        if (animationCount >= 2) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !loading && data && animationCount < 2) {
                    setIsVisible(true);
                    setAnimationCount(prev => prev + 1);
                    setChartKey(prev => prev + 1);
                } else if (!entry.isIntersecting) {
                    setIsVisible(false);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px'
            }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                observer.unobserve(chartRef.current);
            }
        };
    }, [loading, data, animationCount]);

    const flowBreakdown = data?.flow_response_breakdown || [];
    const totalFlowResponses = data?.total_flow_responses || 0;
    const totalTextResponses = data?.total_text_responses || 0;

    // Build labels from parsed screen responses
    const getLabel = (item) => {
        const screens = item?.parsed_response?.screens || [];
        const parts = [];
        screens.forEach((screen) => {
            (screen.responses || []).forEach((resp) => {
                parts.push(resp.answer);
            });
        });
        return parts.join(', ') || 'Unknown';
    };

    const chartData = useMemo(() => {
        if (!flowBreakdown || flowBreakdown.length === 0) return null;

        const labels = flowBreakdown.map(item => getLabel(item));
        const counts = flowBreakdown.map(item => item.count);

        const colors = [
            '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
            '#8b5cf6', '#ec4899', '#14b8a6', '#f97316',
            '#6366f1', '#a855f7', '#94a3b8',
        ];

        return {
            labels,
            datasets: [{
                data: counts,
                backgroundColor: colors.slice(0, flowBreakdown.length),
                borderWidth: 2,
                borderColor: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
                hoverOffset: 4
            }]
        };
    }, [data, theme.palette]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'bottom',
            align: 'center',
            labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 15,
                fontSize: 11,
                fontStyle: '500',
                fontColor: theme.palette.text.primary
            }
        },
        tooltips: {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            titleFontColor: theme.palette.text.primary,
            bodyFontColor: theme.palette.text.secondary,
            borderColor: theme.palette.type === 'dark' ? '#334155' : '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 12,
            displayColors: true,
            bodySpacing: 6,
            titleSpacing: 8,
            caretPadding: 10,
            callbacks: {
                label: (tooltipItem, chartData) => {
                    const dataIndex = tooltipItem.index;
                    const label = chartData.labels[dataIndex] || '';
                    const count = chartData.datasets[0].data[dataIndex] || 0;
                    const uniqueCustomers = flowBreakdown[dataIndex]?.unique_customers || 0;
                    const total = flowBreakdown.reduce((sum, item) => sum + (item.count || 0), 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    return [
                        `${label}`,
                        `Count: ${count.toLocaleString()}`,
                        `Unique Customers: ${uniqueCustomers.toLocaleString()}`,
                        `Percentage: ${percentage}%`
                    ];
                }
            }
        },
        animation: {
            duration: animationCount <= 2 && isVisible ? 2000 : 0,
            easing: 'easeInOutCubic',
            animateRotate: true,
            animateScale: true
        }
    }), [theme.palette, data, isVisible, animationCount]);

    if (loading) {
        return (
            <Paper className={classes.chartCard}>
                <Skeleton variant="text" width="50%" height={35} style={{ marginBottom: '24px' }} animation="wave" />
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '16px' }}>
                    <Skeleton variant="text" width={80} height={40} animation="wave" />
                    <Skeleton variant="text" width={80} height={40} animation="wave" />
                </div>
                <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Skeleton variant="circle" width={240} height={240} animation="wave" />
                </div>
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(item => (
                        <Skeleton key={item} variant="text" width={100} height={20} animation="wave" />
                    ))}
                </div>
            </Paper>
        );
    }

    if (!data || !flowBreakdown || flowBreakdown.length === 0) {
        return (
            <Paper className={classes.chartCard}>
                <Typography className={classes.noDataText}>
                    No flow analytics data available
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            ref={chartRef}
            className={classes.chartCard}
            style={{
                opacity: animationCount >= 2 ? 1 : (isVisible ? 1 : 0.3),
                transform: animationCount >= 2 ? 'scale(1)' : (isVisible ? 'scale(1)' : 'scale(0.95)'),
            }}
        >
            <Typography className={classes.chartTitle}>
                Flow Response Analytics
            </Typography>
            <div className={classes.summaryRow}>
                <div className={classes.summaryItem}>
                    <Typography className={classes.summaryLabel}>Flow Responses</Typography>
                    <Typography className={classes.summaryValue}>{totalFlowResponses.toLocaleString()}</Typography>
                </div>
                <div className={classes.summaryItem}>
                    <Typography className={classes.summaryLabel}>Text Responses</Typography>
                    <Typography className={classes.summaryValue}>{totalTextResponses.toLocaleString()}</Typography>
                </div>
            </div>
            <div style={{ height: '300px' }}>
                {chartData && (
                    <Doughnut
                        key={chartKey}
                        data={chartData}
                        options={chartOptions}
                    />
                )}
            </div>
        </Paper>
    );
}

export default FlowAnalyticsChart;